/**
 * Company Profile Component
 * Allows companies to edit and manage their profile information
 */

import React, { useState, useEffect } from 'react';
import { 
  Building, 
  Mail, 
  Phone, 
  Globe, 
  MapPin, 
  Users, 
  User,
  Camera,
  Save,
  Loader2,
  AlertCircle,
  CheckCircle,
  Clock,
  Edit2,
  ExternalLink,
  Upload,
  X,
  FileText,
  Star,
  Award
} from 'lucide-react';
import useCompanyAuth from '../hooks/useCompanyAuth'; // Import the hook directly

/**
 * Company Profile Component
 * 
 * @returns {JSX.Element} Company profile management interface
 */
const CompanyProfile = () => {
  const { 
    companyProfile, 
    updateProfile, 
    refreshProfile, 
    loading: authLoading,
    isVerified,
    getVerificationStatus,
    getMissingProfileFields
  } = useCompanyAuth(); // Use the hook directly

  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(false);
  const [errors, setErrors] = useState({});
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [formData, setFormData] = useState({
    companyName: '',
    email: '',
    contactPerson: '',
    industry: '',
    companySize: '',
    website: '',
    location: '',
    description: '',
    phone: '',
    foundedYear: '',
    logoUrl: ''
  });

  // Industry options
  const industries = [
    'Technology',
    'Finance',
    'Healthcare',
    'Education',
    'Retail',
    'Manufacturing',
    'Consulting',
    'Marketing',
    'Real Estate',
    'Non-profit',
    'Government',
    'Agriculture',
    'Construction',
    'Entertainment',
    'Energy',
    'Transportation',
    'Food & Beverage',
    'Telecommunications',
    'Automotive',
    'Other'
  ];

  // Company size options
  const companySizes = [
    '1-10 employees',
    '11-50 employees',
    '51-200 employees',
    '201-500 employees',
    '501-1000 employees',
    '1000+ employees'
  ];

  // Initialize form data with profile
  useEffect(() => {
    if (companyProfile) {
      setFormData({
        companyName: companyProfile.companyName || '',
        email: companyProfile.email || '',
        contactPerson: companyProfile.contactPerson || '',
        industry: companyProfile.industry || '',
        companySize: companyProfile.companySize || '',
        website: companyProfile.website || '',
        location: companyProfile.location || '',
        description: companyProfile.description || '',
        phone: companyProfile.phone || '',
        foundedYear: companyProfile.foundedYear || '',
        logoUrl: companyProfile.logoUrl || ''
      });
    }
  }, [companyProfile]);

  // Handle input changes
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    
    // Clear errors when typing
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: undefined }));
    }
  };

  // Validate form
  const validateForm = () => {
    const newErrors = {};
    
    // Required fields
    if (!formData.companyName.trim()) {
      newErrors.companyName = 'Company name is required';
    }
    
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Invalid email address';
    }
    
    if (!formData.contactPerson.trim()) {
      newErrors.contactPerson = 'Contact person is required';
    }
    
    if (!formData.industry) {
      newErrors.industry = 'Please select an industry';
    }
    
    if (!formData.companySize) {
      newErrors.companySize = 'Please select company size';
    }
    
    if (!formData.location.trim()) {
      newErrors.location = 'Location is required';
    }
    
    if (!formData.description.trim()) {
      newErrors.description = 'Company description is required';
    } else if (formData.description.length < 50) {
      newErrors.description = 'Description must be at least 50 characters';
    }
    
    // Optional field validations
    if (formData.website && !formData.website.match(/^https?:\/\/.+/)) {
      newErrors.website = 'Website must be a valid URL (starting with http:// or https://)';
    }
    
    if (formData.phone && !formData.phone.match(/^[\+]?[\d\s\-\(\)]+$/)) {
      newErrors.phone = 'Please enter a valid phone number';
    }
    
    if (formData.foundedYear) {
      const year = parseInt(formData.foundedYear);
      const currentYear = new Date().getFullYear();
      if (year < 1800 || year > currentYear) {
        newErrors.foundedYear = `Founded year must be between 1800 and ${currentYear}`;
      }
    }
    
    return newErrors;
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const newErrors = validateForm();
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }
    
    setLoading(true);
    try {
      await updateProfile(formData);
      setEditing(false);
      setErrors({});
    } catch (error) {
      console.error('Error updating profile:', error);
      setErrors({ general: error.message || 'Failed to update profile' });
    } finally {
      setLoading(false);
    }
  };

  // Handle logo upload (placeholder implementation)
  const handleLogoUpload = async (file) => {
    if (!file) return;
    
    // Validate file type
    if (!file.type.startsWith('image/')) {
      setErrors({ logo: 'Please select an image file' });
      return;
    }
    
    // Validate file size (5MB limit)
    if (file.size > 5 * 1024 * 1024) {
      setErrors({ logo: 'Image must be smaller than 5MB' });
      return;
    }
    
    setUploadingLogo(true);
    try {
      // TODO: Implement actual file upload to Firebase Storage
      // For now, create a local URL
      const logoUrl = URL.createObjectURL(file);
      setFormData(prev => ({ ...prev, logoUrl }));
      setErrors(prev => ({ ...prev, logo: undefined }));
    } catch (error) {
      console.error('Error uploading logo:', error);
      setErrors({ logo: 'Failed to upload logo' });
    } finally {
      setUploadingLogo(false);
    }
  };

  // Get verification status safely
  const verificationStatus = getVerificationStatus ? getVerificationStatus() : { 
    status: 'unknown', 
    message: 'Loading verification status...' 
  };
  
  // Get missing fields safely
  const missingFields = getMissingProfileFields ? getMissingProfileFields() : [];

  if (authLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-500 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading profile...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="container mx-auto px-4 py-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-800 mb-2">Company Profile</h1>
              <p className="text-gray-600">
                Manage your company information and branding
              </p>
            </div>
            
            {!editing && (
              <button
                onClick={() => setEditing(true)}
                className="px-6 py-3 bg-green-500 text-white rounded-xl hover:bg-green-600 transition-colors flex items-center space-x-2"
              >
                <Edit2 className="w-5 h-5" />
                <span>Edit Profile</span>
              </button>
            )}
          </div>
        </div>

        {/* Verification Status */}
        <div className="mb-8">
          <div className={`p-4 rounded-xl border ${
            verificationStatus.status === 'verified' 
              ? 'bg-green-50 border-green-200' 
              : 'bg-yellow-50 border-yellow-200'
          }`}>
            <div className="flex items-center space-x-3">
              {verificationStatus.status === 'verified' ? (
                <CheckCircle className="w-6 h-6 text-green-500" />
              ) : (
                <Clock className="w-6 h-6 text-yellow-500" />
              )}
              <div>
                <h3 className={`font-medium ${
                  verificationStatus.status === 'verified' 
                    ? 'text-green-800' 
                    : 'text-yellow-800'
                }`}>
                  {verificationStatus.status === 'verified' 
                    ? 'Verified Company' 
                    : 'Verification Pending'}
                </h3>
                <p className={`text-sm ${
                  verificationStatus.status === 'verified' 
                    ? 'text-green-700' 
                    : 'text-yellow-700'
                }`}>
                  {verificationStatus.message}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Profile Completeness */}
        {missingFields.length > 0 && (
          <div className="mb-8 bg-blue-50 border border-blue-200 rounded-xl p-4">
            <div className="flex items-start space-x-3">
              <AlertCircle className="w-6 h-6 text-blue-500 mt-0.5" />
              <div>
                <h3 className="font-medium text-blue-800 mb-1">Complete Your Profile</h3>
                <p className="text-blue-700 text-sm mb-2">
                  Please complete the following fields to improve your profile:
                </p>
                <div className="flex flex-wrap gap-2">
                  {missingFields.map((field, index) => (
                    <span 
                      key={index}
                      className="px-2 py-1 bg-blue-100 text-blue-700 text-xs rounded-full"
                    >
                      {field}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Profile Form */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Company Logo */}
                <div className="space-y-4">
                  <h2 className="text-lg font-semibold text-gray-800 flex items-center space-x-2">
                    <Building className="w-5 h-5 text-green-500" />
                    <span>Company Branding</span>
                  </h2>
                  
                  <div className="flex items-center space-x-6">
                    <div className="relative">
                      <div className="w-24 h-24 rounded-xl bg-gray-100 border-2 border-dashed border-gray-300 flex items-center justify-center overflow-hidden">
                        {formData.logoUrl ? (
                          <img 
                            src={formData.logoUrl} 
                            alt="Company Logo" 
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <Building className="w-8 h-8 text-gray-400" />
                        )}
                      </div>
                      
                      {editing && (
                        <label className="absolute -bottom-2 -right-2 w-8 h-8 bg-green-500 text-white rounded-full flex items-center justify-center cursor-pointer hover:bg-green-600 transition-colors">
                          {uploadingLogo ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            <Camera className="w-4 h-4" />
                          )}
                          <input
                            type="file"
                            accept="image/*"
                            onChange={(e) => handleLogoUpload(e.target.files[0])}
                            className="hidden"
                            disabled={uploadingLogo}
                          />
                        </label>
                      )}
                    </div>
                    
                    <div>
                      <h3 className="font-medium text-gray-800">Company Logo</h3>
                      <p className="text-sm text-gray-600 mt-1">
                        Upload a logo to represent your company. Recommended size: 200x200px
                      </p>
                      {errors.logo && (
                        <p className="text-red-500 text-sm mt-1">{errors.logo}</p>
                      )}
                    </div>
                  </div>
                </div>

                {/* Basic Information */}
                <div className="space-y-4">
                  <h3 className="font-medium text-gray-800">Basic Information</h3>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <label className="text-sm font-medium text-gray-700">Company Name *</label>
                      <input
                        type="text"
                        name="companyName"
                        value={formData.companyName}
                        onChange={handleChange}
                        disabled={!editing}
                        className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent ${
                          editing ? 'border-gray-300' : 'border-gray-200 bg-gray-50'
                        } ${errors.companyName ? 'border-red-500' : ''}`}
                      />
                      {errors.companyName && (
                        <p className="text-red-500 text-sm">{errors.companyName}</p>
                      )}
                    </div>

                    <div className="space-y-2">
                      <label className="text-sm font-medium text-gray-700">Industry *</label>
                      <select
                        name="industry"
                        value={formData.industry}
                        onChange={handleChange}
                        disabled={!editing}
                        className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent ${
                          editing ? 'border-gray-300' : 'border-gray-200 bg-gray-50'
                        } ${errors.industry ? 'border-red-500' : ''}`}
                      >
                        <option value="">Select Industry</option>
                        {industries.map((industry) => (
                          <option key={industry} value={industry}>
                            {industry}
                          </option>
                        ))}
                      </select>
                      {errors.industry && (
                        <p className="text-red-500 text-sm">{errors.industry}</p>
                      )}
                    </div>

                    <div className="space-y-2">
                      <label className="text-sm font-medium text-gray-700">Company Size *</label>
                      <select
                        name="companySize"
                        value={formData.companySize}
                        onChange={handleChange}
                        disabled={!editing}
                        className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent ${
                          editing ? 'border-gray-300' : 'border-gray-200 bg-gray-50'
                        } ${errors.companySize ? 'border-red-500' : ''}`}
                      >
                        <option value="">Select Size</option>
                        {companySizes.map((size) => (
                          <option key={size} value={size}>
                            {size}
                          </option>
                        ))}
                      </select>
                      {errors.companySize && (
                        <p className="text-red-500 text-sm">{errors.companySize}</p>
                      )}
                    </div>

                    <div className="space-y-2">
                      <label className="text-sm font-medium text-gray-700">Founded Year</label>
                      <input
                        type="number"
                        name="foundedYear"
                        value={formData.foundedYear}
                        onChange={handleChange}
                        disabled={!editing}
                        min="1800"
                        max={new Date().getFullYear()}
                        className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent ${
                          editing ? 'border-gray-300' : 'border-gray-200 bg-gray-50'
                        } ${errors.foundedYear ? 'border-red-500' : ''}`}
                      />
                      {errors.foundedYear && (
                        <p className="text-red-500 text-sm">{errors.foundedYear}</p>
                      )}
                    </div>
                  </div>
                </div>

                {/* Contact Information */}
                <div className="space-y-4">
                  <h3 className="font-medium text-gray-800">Contact Information</h3>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <label className="text-sm font-medium text-gray-700">Email *</label>
                      <div className="relative">
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                          <Mail className="h-5 w-5 text-gray-400" />
                        </div>
                        <input
                          type="email"
                          name="email"
                          value={formData.email}
                          onChange={handleChange}
                          disabled={!editing}
                          className={`pl-10 pr-4 py-2 w-full border rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent ${
                            editing ? 'border-gray-300' : 'border-gray-200 bg-gray-50'
                          } ${errors.email ? 'border-red-500' : ''}`}
                        />
                      </div>
                      {errors.email && (
                        <p className="text-red-500 text-sm">{errors.email}</p>
                      )}
                    </div>

                    <div className="space-y-2">
                      <label className="text-sm font-medium text-gray-700">Phone</label>
                      <div className="relative">
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                          <Phone className="h-5 w-5 text-gray-400" />
                        </div>
                        <input
                          type="tel"
                          name="phone"
                          value={formData.phone}
                          onChange={handleChange}
                          disabled={!editing}
                          className={`pl-10 pr-4 py-2 w-full border rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent ${
                            editing ? 'border-gray-300' : 'border-gray-200 bg-gray-50'
                          } ${errors.phone ? 'border-red-500' : ''}`}
                        />
                      </div>
                      {errors.phone && (
                        <p className="text-red-500 text-sm">{errors.phone}</p>
                      )}
                    </div>

                    <div className="space-y-2">
                      <label className="text-sm font-medium text-gray-700">Contact Person *</label>
                      <div className="relative">
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                          <User className="h-5 w-5 text-gray-400" />
                        </div>
                        <input
                          type="text"
                          name="contactPerson"
                          value={formData.contactPerson}
                          onChange={handleChange}
                          disabled={!editing}
                          className={`pl-10 pr-4 py-2 w-full border rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent ${
                            editing ? 'border-gray-300' : 'border-gray-200 bg-gray-50'
                          } ${errors.contactPerson ? 'border-red-500' : ''}`}
                        />
                      </div>
                      {errors.contactPerson && (
                        <p className="text-red-500 text-sm">{errors.contactPerson}</p>
                      )}
                    </div>

                    <div className="space-y-2">
                      <label className="text-sm font-medium text-gray-700">Website</label>
                      <div className="relative">
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                          <Globe className="h-5 w-5 text-gray-400" />
                        </div>
                        <input
                          type="url"
                          name="website"
                          value={formData.website}
                          onChange={handleChange}
                          disabled={!editing}
                          placeholder="https://www.company.com"
                          className={`pl-10 pr-4 py-2 w-full border rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent ${
                            editing ? 'border-gray-300' : 'border-gray-200 bg-gray-50'
                          } ${errors.website ? 'border-red-500' : ''}`}
                        />
                      </div>
                      {errors.website && (
                        <p className="text-red-500 text-sm">{errors.website}</p>
                      )}
                    </div>
                  </div>
                </div>

                {/* Location */}
                <div className="space-y-2">
                  <label className="text-sm font-medium text-gray-700">Location *</label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <MapPin className="h-5 w-5 text-gray-400" />
                    </div>
                    <input
                      type="text"
                      name="location"
                      value={formData.location}
                      onChange={handleChange}
                      disabled={!editing}
                      placeholder="Cape Town, South Africa"
                      className={`pl-10 pr-4 py-2 w-full border rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent ${
                        editing ? 'border-gray-300' : 'border-gray-200 bg-gray-50'
                      } ${errors.location ? 'border-red-500' : ''}`}
                    />
                  </div>
                  {errors.location && (
                    <p className="text-red-500 text-sm">{errors.location}</p>
                  )}
                </div>

                {/* Company Description */}
                <div className="space-y-2">
                  <label className="text-sm font-medium text-gray-700">Company Description *</label>
                  <textarea
                    name="description"
                    value={formData.description}
                    onChange={handleChange}
                    disabled={!editing}
                    rows={6}
                    placeholder="Tell us about your company, culture, mission, and what makes you unique..."
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent resize-none ${
                      editing ? 'border-gray-300' : 'border-gray-200 bg-gray-50'
                    } ${errors.description ? 'border-red-500' : ''}`}
                  />
                  <div className="flex justify-between items-center">
                    {errors.description && (
                      <p className="text-red-500 text-sm">{errors.description}</p>
                    )}
                    <p className="text-xs text-gray-500 ml-auto">
                      {formData.description.length} characters (minimum 50)
                    </p>
                  </div>
                </div>

                {/* General Error */}
                {errors.general && (
                  <div className="bg-red-50 border border-red-200 rounded-xl p-4">
                    <div className="flex items-start space-x-3">
                      <AlertCircle className="w-5 h-5 text-red-500 mt-0.5" />
                      <div>
                        <p className="text-red-800 font-medium">Error</p>
                        <p className="text-red-700 text-sm">{errors.general}</p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Form Actions */}
                {editing && (
                  <div className="flex items-center justify-between pt-6 border-t border-gray-200">
                    <button
                      type="button"
                      onClick={() => {
                        setEditing(false);
                        setErrors({});
                        // Reset form data
                        if (companyProfile) {
                          setFormData({
                            companyName: companyProfile.companyName || '',
                            email: companyProfile.email || '',
                            contactPerson: companyProfile.contactPerson || '',
                            industry: companyProfile.industry || '',
                            companySize: companyProfile.companySize || '',
                            website: companyProfile.website || '',
                            location: companyProfile.location || '',
                            description: companyProfile.description || '',
                            phone: companyProfile.phone || '',
                            foundedYear: companyProfile.foundedYear || '',
                            logoUrl: companyProfile.logoUrl || ''
                          });
                        }
                      }}
                      className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                    >
                      Cancel
                    </button>
                    
                    <button
                      type="submit"
                      disabled={loading}
                      className={`px-6 py-2 rounded-lg font-medium flex items-center space-x-2 transition-colors ${
                        loading
                          ? 'bg-gray-300 cursor-not-allowed'
                          : 'bg-green-500 hover:bg-green-600 text-white'
                      }`}
                    >
                      {loading ? (
                        <>
                          <Loader2 className="w-4 h-4 animate-spin" />
                          <span>Saving...</span>
                        </>
                      ) : (
                        <>
                          <Save className="w-4 h-4" />
                          <span>Save Changes</span>
                        </>
                      )}
                    </button>
                  </div>
                )}
              </form>
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Profile Summary */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <h3 className="font-semibold text-gray-800 mb-4">Profile Summary</h3>
              
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">Profile Completeness</span>
                  <span className="text-sm font-medium text-gray-800">
                    {Math.round(((8 - missingFields.length) / 8) * 100)}%
                  </span>
                </div>
                
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div 
                    className="bg-green-500 h-2 rounded-full transition-all duration-300"
                    style={{ width: `${((8 - missingFields.length) / 8) * 100}%` }}
                  ></div>
                </div>
                
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">Verification Status</span>
                  <span className={`text-sm font-medium ${
                    isVerified ? 'text-green-600' : 'text-yellow-600'
                  }`}>
                    {isVerified ? 'Verified' : 'Pending'}
                  </span>
                </div>
              </div>
            </div>

            {/* Quick Actions */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <h3 className="font-semibold text-gray-800 mb-4">Quick Actions</h3>
              
              <div className="space-y-3">
                {formData.website && (
                  <a
                    href={formData.website}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center space-x-2 text-sm text-green-600 hover:text-green-700"
                  >
                    <ExternalLink className="w-4 h-4" />
                    <span>Visit Website</span>
                  </a>
                )}
                
                <button
                  onClick={refreshProfile}
                  className="flex items-center space-x-2 text-sm text-gray-600 hover:text-gray-800"
                >
                  <FileText className="w-4 h-4" />
                  <span>Refresh Profile</span>
                </button>
                
                <button
                  onClick={() => window.print()}
                  className="flex items-center space-x-2 text-sm text-gray-600 hover:text-gray-800"
                >
                  <Upload className="w-4 h-4" />
                  <span>Export Profile</span>
                </button>
              </div>
            </div>

            {/* Company Stats */}
            {companyProfile && (
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                <h3 className="font-semibold text-gray-800 mb-4">Company Information</h3>
                
                <div className="space-y-3 text-sm">
                  <div className="flex items-center justify-between">
                    <span className="text-gray-600">Member Since</span>
                    <span className="font-medium text-gray-800">
                      {companyProfile.createdAt ? 
                        new Date(companyProfile.createdAt).toLocaleDateString('en-ZA', {
                          year: 'numeric',
                          month: 'short'
                        }) : 'N/A'
                      }
                    </span>
                  </div>
                  
                  <div className="flex items-center justify-between">
                    <span className="text-gray-600">Last Updated</span>
                    <span className="font-medium text-gray-800">
                      {companyProfile.updatedAt ? 
                        new Date(companyProfile.updatedAt).toLocaleDateString('en-ZA', {
                          year: 'numeric',
                          month: 'short',
                          day: 'numeric'
                        }) : 'N/A'
                      }
                    </span>
                  </div>
                  
                  <div className="flex items-center justify-between">
                    <span className="text-gray-600">Account Type</span>
                    <span className="font-medium text-gray-800 flex items-center space-x-1">
                      <Building className="w-3 h-3" />
                      <span>Company</span>
                    </span>
                  </div>
                </div>
              </div>
            )}

            {/* Tips */}
            <div className="bg-blue-50 rounded-xl border border-blue-200 p-6">
              <h3 className="font-semibold text-blue-800 mb-3 flex items-center space-x-2">
                <Star className="w-5 h-5" />
                <span>Profile Tips</span>
              </h3>
              
              <div className="space-y-3 text-sm text-blue-700">
                <div className="flex items-start space-x-2">
                  <div className="w-1.5 h-1.5 bg-blue-500 rounded-full mt-2 flex-shrink-0"></div>
                  <span>Complete all required fields to improve your profile visibility</span>
                </div>
                <div className="flex items-start space-x-2">
                  <div className="w-1.5 h-1.5 bg-blue-500 rounded-full mt-2 flex-shrink-0"></div>
                  <span>Add a professional company logo to build trust with candidates</span>
                </div>
                <div className="flex items-start space-x-2">
                  <div className="w-1.5 h-1.5 bg-blue-500 rounded-full mt-2 flex-shrink-0"></div>
                  <span>Write a detailed company description to attract the right talent</span>
                </div>
                <div className="flex items-start space-x-2">
                  <div className="w-1.5 h-1.5 bg-blue-500 rounded-full mt-2 flex-shrink-0"></div>
                  <span>Verification is required to post job opportunities</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CompanyProfile;